import { isAnswerObject, type Question } from './mathWordProblemsTypes';

export const markdownStyles = `
  .markdown-body {
    font-family: Arial, sans-serif;
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
  }
  .markdown-body h1, .markdown-body h2, .markdown-body h3 {
    color: #333;
    font-weight: bold;
  }
  .markdown-body p {
    margin-bottom: 1em;
  }
  .question-block {
    margin-bottom: 1.5em;
    page-break-inside: avoid;
  }
  .answer-choices {
    margin-left: 1.5em;
  }
  .answer-key-section {
    page-break-before: always;
    margin-top: 2em;
    padding-top: 1em;
    border-top: 2px solid #333;
  }
`;

export const PRINT_MEDIA_STYLES = `
  @media print {
    form,
    button,
    .MuiButton-root,
    .MuiIconButton-root,
    .MuiChip-root,
    .no-print,
    nav,
    header,
    footer {
      display: none !important;
    }
    #math-problems-content {
      display: block !important;
    }
    .question-block,
    .question {
      page-break-inside: avoid !important;
    }
    div[style*="display: flex"] {
      page-break-inside: avoid !important;
    }
    div[style*="border-top"][style*="2px solid"] {
      page-break-before: always !important;
    }
  }
`;

export function parseQuestionsFromContent(content: string): Question[] {
  if (!content) return [];
  try {
    const trimmed = content.trim();
    if (trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? (parsed as Question[]) : [];
    }
    const jsonMatch = trimmed.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return Array.isArray(parsed) ? (parsed as Question[]) : [];
    }
  } catch {
    return [];
  }
  return [];
}

export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function convertLatexToPlainText(latexExpr: string): string {
  let s = latexExpr.trim();
  if (s.startsWith('$$') && s.endsWith('$$')) s = s.slice(2, -2);
  else if (s.startsWith('$') && s.endsWith('$') && s.length > 1) s = s.slice(1, -1);
  else if (s.startsWith('\\[') && s.endsWith('\\]')) s = s.slice(2, -2);
  else if (s.startsWith('\\(') && s.endsWith('\\)')) s = s.slice(2, -2);
  s = s.trim();
  s = s.replace(/\\text\s*\{([^}]*)\}/g, '$1');
  s = s.replace(/\^1(?!\d)/g, '¹').replace(/\^2(?!\d)/g, '²').replace(/\^3(?!\d)/g, '³');
  s = s.replace(/\\times/g, '×').replace(/\\div/g, '÷').replace(/\\pm/g, '±');
  s = s.replace(/\\frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, '$1/$2');
  return s.trim();
}

export function processMathContent(text: string, forWord?: boolean): string {
  if (!text) return '';

  const latexPlaceholders: string[] = [];
  let placeholderIndex = 0;

  const latexPatterns = [
    /\$\$[\s\S]*?\$\$/g,
    /\$[^$\n]*?\$/g,
    /\\\[[\s\S]*?\\\]/g,
    /\\\([\s\S]*?\\\)/g,
  ];

  let processed = text;

  latexPatterns.forEach((pattern) => {
    processed = processed.replace(pattern, (match) => {
      const placeholder = `__LATEX_PLACEHOLDER_${placeholderIndex}__`;
      latexPlaceholders[placeholderIndex] = match;
      placeholderIndex++;
      return placeholder;
    });
  });

  processed = escapeHtml(processed);

  latexPlaceholders.forEach((latex, index) => {
    const value = forWord ? convertLatexToPlainText(latex) : latex;
    processed = processed.replace(`__LATEX_PLACEHOLDER_${index}__`, value);
  });

  processed = processed.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
  processed = processed.replace(/(?<!<[^>]*)\*([^*<>]+?)\*(?![^<]*>)/g, '<em>$1</em>');
  processed = processed.replace(/\n\n+/g, '</p><p>');
  processed = processed.replace(/\n/g, '<br>');

  return processed;
}

export function processAnswerKeyContent(text: string, forWord?: boolean): string {
  if (!text) return '';

  const latexPlaceholders: string[] = [];
  let placeholderIndex = 0;

  const latexPatterns = [
    /\$\$[\s\S]*?\$\$/g,
    /\$[^$\n]*?\$/g,
    /\\\[[\s\S]*?\\\]/g,
    /\\\([\s\S]*?\\\)/g,
  ];

  let processed = text;

  latexPatterns.forEach((pattern) => {
    processed = processed.replace(pattern, (match) => {
      const placeholder = `__LATEX_PLACEHOLDER_${placeholderIndex}__`;
      latexPlaceholders[placeholderIndex] = match;
      placeholderIndex++;
      return placeholder;
    });
  });

  const lines = processed.split('\n');
  const output: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';

    if (line.match(/^---+$/)) {
      if (inList) {
        output.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      output.push('<hr style="margin: 1.5em 0; border: none; border-top: 1px solid #e0e0e0;">');
      continue;
    }

    if (line.startsWith('### ')) {
      if (inList) {
        output.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      const headingText = escapeHtml(line.substring(4));
      output.push(`<h3 style="font-size: 1.1rem; font-weight: 700; margin-top: 1em; margin-bottom: 0.5em; color: #9c27b0;">${headingText}</h3>`);
      continue;
    }

    if (line.startsWith('## ')) {
      if (inList) {
        output.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      const headingText = escapeHtml(line.substring(3));
      output.push(`<h2 style="font-size: 1.25rem; font-weight: 700; margin-top: 1.5em; margin-bottom: 0.75em; color: #9c27b0; border-bottom: 1px solid #e0e0e0; padding-bottom: 0.5em;">${headingText}</h2>`);
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s*(.+)$/);
    if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        if (inList && listType === 'ol') {
          output.push('</ol>');
        }
        output.push('<ul style="margin-left: 1.5em; margin-bottom: 0.75em; padding-left: 1.5em;">');
        inList = true;
        listType = 'ul';
      }
      const listItem = escapeHtml(bulletMatch[1].trim());
      if (listItem) {
        output.push(`<li style="margin-bottom: 0.5em; line-height: 1.6;">${listItem}</li>`);
      }
      continue;
    }

    if (line.match(/^\d+\.\s+/)) {
      if (!inList || listType !== 'ol') {
        if (inList && listType === 'ul') {
          output.push('</ul>');
        }
        output.push('<ol style="margin-left: 1.5em; margin-bottom: 0.75em; padding-left: 1.5em;">');
        inList = true;
        listType = 'ol';
      }
      const listItem = escapeHtml(line.replace(/^\d+\.\s+/, ''));
      output.push(`<li style="margin-bottom: 0.5em; line-height: 1.6;">${listItem}</li>`);
      continue;
    }

    if (line === '') {
      if (inList) {
        output.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      if (nextLine && !nextLine.match(/^[-*]\s+/) && !nextLine.match(/^\d+\.\s+/) && !nextLine.startsWith('#')) {
        output.push('<p style="margin-bottom: 0.75em;"></p>');
      }
      continue;
    }

    if (inList) {
      output.push(`</${listType}>`);
      inList = false;
      listType = null;
    }

    let paraText = escapeHtml(line);
    paraText = paraText.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    paraText = paraText.replace(/(?<!<[^>]*)\*([^*<>]+?)\*(?![^<]*>)/g, '<em>$1</em>');
    output.push(`<p style="margin-bottom: 0.75em; line-height: 1.6;">${paraText}</p>`);
  }

  if (inList && listType) {
    output.push(`</${listType}>`);
  }

  let result = output.join('\n');
  latexPlaceholders.forEach((latex, index) => {
    const value = forWord ? convertLatexToPlainText(latex) : latex;
    result = result.replace(`__LATEX_PLACEHOLDER_${index}__`, value);
  });

  return result;
}

export function createPrintContent(
  questions: Question[],
  answerKey: string | undefined,
  gradeLevel: string,
  format: 'print' | 'pdf' | 'word' = 'print',
  graphImages?: Map<number, string>,
): string {
  let content = '';
  content += `<h1>Math Word Problems - Grade ${gradeLevel}</h1>`;

  const forWord = format === 'word';
  questions.forEach((q, index) => {
    content += `<div class="question">`;
    content += `<h2>Question ${index + 1}</h2>`;

    const questionText = q.question || '';
    const processedQuestion = processMathContent(questionText, forWord);
    content += `<div class="question-text"><p>${processedQuestion}</p></div>`;

    if (q.graph && q.graph.type) {
      if (graphImages && graphImages.has(index)) {
        const imgDataUrl = graphImages.get(index)!;
        content += `<div style="text-align: center; margin: 1em 0;"><img src="${imgDataUrl}" style="max-width: 400px; max-height: 400px; width: auto; height: auto; display: block; margin: 10px auto;" /></div>`;
      } else {
        content += `<div class="graph-placeholder">[Graph visualization]</div>`;
      }
    }

    if (q.answers && q.answers.length > 0) {
      content += `<div class="answer-choices">`;
      q.answers.forEach((answer, j) => {
        let cleanAnswer = isAnswerObject(answer) ? answer.text : String(answer);
        cleanAnswer = cleanAnswer.replace(/^[A-Da-d][\.\)\:\s]+\s*/, '');
        const processedAnswer = processMathContent(cleanAnswer, forWord);
        const letter = String.fromCharCode(65 + j);
        content += `
            <div class="answer-choice">
              <span class="answer-letter">${letter}.</span>
              <span class="answer-text">${processedAnswer}</span>
            </div>
          `;
      });
      content += `</div>`;
    }

    content += `</div>`;
  });

  if (answerKey) {
    content += `<div class="answer-key-section">`;
    content += `<h2>Comprehensive Answer Key</h2>`;
    const processedAnswerKey = processAnswerKeyContent(answerKey, forWord);
    content += `<div style="line-height: 1.8;">${processedAnswerKey}</div>`;
    content += `</div>`;
  } else if (questions.length > 0) {
    content += `<div class="answer-key-section">`;
    content += `<h2>Answer Key</h2>`;
    content += `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.5em;">`;
    questions.forEach((q, idx) => {
      const processedAnswer = processMathContent(q.correctAnswer, forWord);
      content += `<div>${idx + 1}. ${processedAnswer}</div>`;
    });
    content += `</div></div>`;
  }

  return content;
}

export async function svgToImageDataUrl(svg: SVGElement): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 400;
        const maxHeight = 400;

        let width = maxWidth;
        let height = maxHeight;

        if (svg instanceof SVGSVGElement) {
          const svgEl = svg as SVGSVGElement;
          if (svgEl.width && svgEl.width.baseVal) {
            width = svgEl.width.baseVal.value;
          }
          if (svgEl.height && svgEl.height.baseVal) {
            height = svgEl.height.baseVal.value;
          }
        }

        if (!width || width === maxWidth) {
          const viewBox = svg.getAttribute('viewBox');
          if (viewBox) {
            const parts = viewBox.split(' ');
            if (parts.length >= 4) {
              width = parseFloat(parts[2]) || img.width || maxWidth;
              height = parseFloat(parts[3]) || img.height || maxHeight;
            }
          } else {
            width = img.width || maxWidth;
            height = img.height || maxHeight;
          }
        }

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/png');
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        } else {
          URL.revokeObjectURL(url);
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load SVG as image'));
      };
      img.src = url;
    } catch (err) {
      reject(err);
    }
  });
}
